/**
 * Power Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de potência.
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - SI Brochure, 9th Edition (2019)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type PowerUnit =
  // SI
  | "gigawatt"
  | "megawatt"
  | "kilowatt"
  | "watt"
  | "milliwatt"
  | "microwatt"
  // Horsepower
  | "horsepower_mechanical"
  | "horsepower_metric"
  | "horsepower_electric"
  | "horsepower_boiler"
  // BTU-based
  | "btu_per_hour"
  | "btu_per_second"
  // Other
  | "ton_of_refrigeration"
  | "foot_pound_per_second"
  | "calorie_per_second"
  | "kilocalorie_per_hour";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de potência.
 *
 * Base: watt (W) - unidade SI de potência
 * 1 watt = 1 joule por segundo = 1 kg⋅m²/s³
 *
 * Cavalos-vapor (existem várias definições):
 * - Mechanical (imperial): 550 ft⋅lbf/s = 745.69987158227 W
 * - Metric (PS, CV, pk): 75 kgf⋅m/s = 735.49875 W (exato)
 * - Electrical: 746 W (exato por definição)
 * - Boiler: 9809.5 W (33,475 BTU/h)
 */
export const POWER_UNITS: Record<PowerUnit, SimpleUnitConfig> = {
  // SI Units (todos exatos)
  gigawatt: {
    factor: 1_000_000_000,
    symbol: "GW",
    singular: "gigawatt",
    plural: "gigawatts",
  },
  megawatt: {
    factor: 1_000_000,
    symbol: "MW",
    singular: "megawatt",
    plural: "megawatts",
  },
  kilowatt: {
    factor: 1_000,
    symbol: "kW",
    singular: "kilowatt",
    plural: "kilowatts",
  },
  watt: {
    factor: 1,
    symbol: "W",
    singular: "watt",
    plural: "watts",
  },
  milliwatt: {
    factor: 0.001,
    symbol: "mW",
    singular: "milliwatt",
    plural: "milliwatts",
  },
  microwatt: {
    factor: 0.000001,
    symbol: "μW",
    singular: "microwatt",
    plural: "microwatts",
  },

  // Horsepower variants
  // Mechanical (imperial): 550 ft⋅lbf/s
  // = 550 × 0.3048 m × 4.4482216152605 N / s = 745.69987158227 W
  horsepower_mechanical: {
    factor: 745.69987158227,
    symbol: "hp",
    singular: "horsepower",
    plural: "horsepower",
  },
  // Metric (PS, CV, pk): 75 kgf⋅m/s = 75 × 9.80665 W = 735.49875 W (exato)
  horsepower_metric: {
    factor: 735.49875,
    symbol: "PS",
    singular: "metric horsepower",
    plural: "metric horsepower",
  },
  // Electrical: 746 W (exato por definição)
  horsepower_electric: {
    factor: 746,
    symbol: "hp(E)",
    singular: "electric horsepower",
    plural: "electric horsepower",
  },
  // Boiler: 33,475 BTU/h = 9809.5 W
  horsepower_boiler: {
    factor: 9809.5,
    symbol: "hp(S)",
    singular: "boiler horsepower",
    plural: "boiler horsepower",
  },

  // BTU-based
  // 1 BTU/h = 1055.05585262 J / 3600 s = 0.29307107017222 W
  btu_per_hour: {
    factor: 0.29307107017222,
    symbol: "BTU/h",
    singular: "BTU per hour",
    plural: "BTUs per hour",
  },
  // 1 BTU/s = 1055.05585262 W
  btu_per_second: {
    factor: 1055.05585262,
    symbol: "BTU/s",
    singular: "BTU per second",
    plural: "BTUs per second",
  },

  // Other
  // 1 ton of refrigeration = 12000 BTU/h = 3516.8528420667 W
  ton_of_refrigeration: {
    factor: 3516.8528420667,
    symbol: "TR",
    singular: "ton of refrigeration",
    plural: "tons of refrigeration",
  },
  // 1 ft⋅lbf/s = 1.3558179483314004 W
  foot_pound_per_second: {
    factor: 1.3558179483314004,
    symbol: "ft⋅lbf/s",
    singular: "foot-pound per second",
    plural: "foot-pounds per second",
  },
  // 1 cal/s = 4.1868 W
  calorie_per_second: {
    factor: 4.1868,
    symbol: "cal/s",
    singular: "calorie per second",
    plural: "calories per second",
  },
  // 1 kcal/h = 4186.8 / 3600 = 1.163 W
  kilocalorie_per_hour: {
    factor: 1.163,
    symbol: "kcal/h",
    singular: "kilocalorie per hour",
    plural: "kilocalories per hour",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const POWER_ALIASES: UnitAliases = {
  // gigawatt
  gw: "gigawatt",
  // megawatt
  mw: "megawatt",
  // kilowatt
  kw: "kilowatt",
  // watt
  w: "watt",
  // microwatt
  "μw": "microwatt",
  uw: "microwatt",
  // horsepower_mechanical
  hp: "horsepower_mechanical",
  // horsepower_metric
  ps: "horsepower_metric",
  cv: "horsepower_metric",
  // horsepower_electric
  "hp(e)": "horsepower_electric",
  // horsepower_boiler
  "hp(s)": "horsepower_boiler",
  bhp: "horsepower_boiler",
  // btu_per_hour
  "btu/h": "btu_per_hour",
  "btu/hr": "btu_per_hour",
  // btu_per_second
  "btu/s": "btu_per_second",
  // ton_of_refrigeration
  tr: "ton_of_refrigeration",
  rt: "ton_of_refrigeration",
  // foot_pound_per_second
  "ft-lb/s": "foot_pound_per_second",
  "ft-lbf/s": "foot_pound_per_second",
  // calorie_per_second
  "cal/s": "calorie_per_second",
  // kilocalorie_per_hour
  "kcal/h": "kilocalorie_per_hour",
  "kcal/hr": "kilocalorie_per_hour",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const POWER_UNIT_LIST = Object.keys(POWER_UNITS) as PowerUnit[];

/** Fatores de conversão (para compatibilidade) */
export const POWER_FACTORS: Record<PowerUnit, number> = Object.fromEntries(
  Object.entries(POWER_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<PowerUnit, number>;
